<template>
    <van-overlay :show="show" z-index="2000">
        <div class="edit-popup-container">

            <!-- 编辑框 -->
            <div class="edit-textarea-wrapper rounded-20px p-16px" @touchmove.stop>
                <textarea ref="editTextareaRef" v-model="localContent" @touchmove.stop @blur="handleTextareaBlur"
                    class="edit-textarea w-100% h-180px color-#fff text-14px leading-22px bg-transparent border-none outline-none resize-none"
                    placeholder="Edit your message..."></textarea>
            </div>

            <!-- 操作栏 -->
            <div class="edit-actions bg-#2C2D36! p-0px px-15px flex items-center justify-between mt-16px" @mousedown.prevent>
                <div class="flex items-center gap-16px" @mousedown.prevent>
                    <!-- <div @click="handleUndo" @mousedown.prevent @touchmove.stop class="edit-action-btn "
                        :class="{ 'opacity-50 cursor-not-allowed': undoStack.length <= 1 }">
                        <img src="@/assets/images/message/chehui.svg" alt="" class="w-22px h-22px">
                    </div>
                    <div @click="handleRedo" @mousedown.prevent @touchmove.stop class="edit-action-btn"
                        :class="{ 'opacity-50 cursor-not-allowed': redoStack.length === 0 }">
                        <img src="@/assets/images/message/qianjin.svg" alt="" class="w-22px h-22px">
                    </div> -->
                    <div @click="handlePaste" @mousedown.prevent @touchmove.stop class="edit-action-btn">
                        <img src="@/assets/images/message/pagbai.svg" alt="" class="w-22px h-22px">
                    </div>
                </div>
                <div class="flex items-center gap-10px">
                    <div @click="handleCancel"
                        class="edit-cancel-btn px-20px py-10px rounded-20px color-#999 text-14px">
                        Cancel
                    </div>
                    <div @click="handleSave"
                        class="edit-save-btn px-20px py-10px rounded-20px color-#fff text-14px font-600">
                        Save
                    </div>
                </div>
            </div>
        </div>
    </van-overlay>
</template>

<script lang="ts" setup>
import { ref, watch, nextTick } from 'vue'
import { useMarkdown } from '@/composables/useMarkdown';

const { extractThinking, } = useMarkdown();
interface Props {
    show: boolean
    message?: any
    content?: string
    name?: string
    avatar?: string
    defaultAvatar?: string
}

interface Emits {
    (e: 'update:show', value: boolean): void
    (e: 'save', content: string): void
    (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
    message: null,
    content: '',
    name: '',
    avatar: '',
    defaultAvatar: '/assets/images/discover/dome.png'
})

const emit = defineEmits<Emits>()

const editTextareaRef = ref<HTMLTextAreaElement>()
const localContent = ref('')
const undoStack = ref<string[]>([])
const redoStack = ref<string[]>([])

// 初始化
const init = async () => {
    localContent.value = getMessageContent(props.message) || ''
    undoStack.value = [localContent.value]
    redoStack.value = []

    // 等待 DOM 更新后聚焦并弹出键盘
    await nextTick()
    editTextareaRef.value?.focus()
}
// 🔧 新增：提取消息的思考内容
const getThinkingContent = (message: any) => {
    const rawContent = message?.content;
    if (rawContent) {
        return `<thinking>${extractThinking(rawContent).thinking}</thinking>`;
    } else {
        return '';
    }
}
// 🔧 新增：获取消息的正常内容（不含思考）
const getMessageContent = (message: any) => {
    const rawContent = message?.content;
    // 去掉 <content></content> 标签
    const cleanContent = rawContent?.replace(/<\/?content>/g, '') || '';
    return extractThinking(cleanContent).content;
};

// 监听弹窗显示
watch(() => props.show, (newVal) => {
    if (newVal) {
        init()
    }
})

// 监听内容变化
watch(localContent, (newVal, oldVal) => {
    if (props.show && newVal !== oldVal && oldVal !== '') {
        undoStack.value.push(newVal)
        if (undoStack.value.length > 50) {
            undoStack.value.shift()
        }
        redoStack.value = []
    }
})

// 撤销
const handleUndo = () => {
    if (undoStack.value.length > 1) {
        const current = undoStack.value.pop()
        redoStack.value.push(current!)
        localContent.value = undoStack.value[undoStack.value.length - 1]
    }
    // 保持焦点
    editTextareaRef.value?.focus()
}

// 重做
const handleRedo = () => {
    if (redoStack.value.length > 0) {
        const last = redoStack.value.pop()!
        undoStack.value.push(last)
        localContent.value = last
    }
    // 保持焦点
    editTextareaRef.value?.focus()
}

// 插入粗体标记
const handlePaste = () => {
    const textarea = editTextareaRef.value
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const text = localContent.value

    // 在选区位置插入 **
    const newText = text.substring(0, start) + '**' + text.substring(end)
    localContent.value = newText

    // 恢复焦点并设置光标位置
    nextTick(() => {
        textarea.focus()
        textarea.setSelectionRange(start + 2, start + 2)
    })
}

// 保存
const handleSave = () => {
    const content = localContent.value.trim()
    if (content) {
        emit('save', getThinkingContent(props.message) + content)
    }
    emit('update:show', false)
}

// 取消
const handleCancel = () => {
    emit('cancel')
    emit('update:show', false)
}

// 点击遮罩关闭
const handleOverlayClick = () => {
    handleCancel()
}

// 文本框失去焦点时关闭弹窗
const handleTextareaBlur = () => {
    handleCancel()
}
</script>

<style scoped lang="scss">
.van-overlay {
    background: rgba(0, 0, 0, 0.6) !important;
    backdrop-filter: blur(4px);
}

.edit-popup-container {
    position: absolute;
    bottom: 10px;
    left: 0;
    right: 0;
    background: #01031B;
    border-radius: 20px;
    // padding: 20px;
    width: 90%;
    margin: 0 auto;
    border: 1px solid #fff;
}

.edit-character-info {
    padding-bottom: 10px;
}

.edit-textarea-wrapper {
    background: #01031B;
    max-height: 180px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
    }
}

.edit-textarea {
    font-family: inherit;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
}

.edit-action-btn {
    // background: rgba(255, 255, 255, 0.1);
    // cursor: pointer;

    // &:active {
    //     background: rgba(255, 255, 255, 0.2);
    // }
}
.edit-actions{
    border-radius: 0px 0px 20px 20px;
}

.edit-cancel-btn {
    // background: rgba(255, 255, 255, 0.1);
    // cursor: pointer;
    font-size: 15px;
    color: #FFFFFF;

    &:active {
        // background: rgba(255, 255, 255, 0.2);
    }
}

.edit-save-btn {
    width: 85px;
    height: 28px;
    background: linear-gradient(0deg, #5A68FF 0%, #95A5FF 100%);
    border-radius: 14px;
    font-weight: 500;
    font-size: 12px;
    color: #FFFFFF;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
